import { Item } from "@react-stately/collections";
import type { Node } from "@react-types/shared";
import { clsx } from "clsx";
import { atom, useAtom } from "jotai";
import { groupBy, values } from "ramda";
import { type FC, type ReactElement, useCallback, useEffect, useState } from "react";
import { mergeProps, useFocusRing, useHover } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { type SingleSelectListProps, type TabListState } from "react-stately";
import { ActionButton, Card, HeaderBar, LoadingSpinner, useNoSelectionTabList, useTab, useTabPanel } from "widgets";
import { useURLSearchParamSingleValue, useVisuallyLoading } from "widgets/hooks";

import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import {
  getIconForPlaybookCategory,
  isPlaybookExecutableInPlaybookPageContext,
  loadPlaybooks,
} from "@/app-utils/playbook.ts";
import SelectorPlaybooksArrow from "@/assets/img/playbook_category_arrow.svg?react";
import { type Configuration, type Playbook, type PlaybookCategory, type PlaybookEvent } from "@/graphql.ts";
import { PlaybookFilter } from "@/modules/playbooks/components/PlaybookFilter.tsx";
import { loadEvents } from "@/redux/modules/event.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ModulesIcons } from "@/settings/assets-settings.ts";

import { ContentPlaceholder } from "../../core/ContentPlaceholder.js";
import styleLayout from "../../core/styles/PageLayout.module.scss";
import { PlaybookLauncherTileAsMenu } from "../../core/tiles/components/PlaybookLauncherTile.js";
import { PlaybookEventTile, PlaybookLauncherTile } from "../../core/tiles/index.js";
import style from "../styles/PlaybooksPage.module.scss";

function Tab({ item, state }: { item: Node<ReactElement>; state: TabListState<ReactElement> }) {
  const { ref, rendered, tabProps, isSelected } = useTab<HTMLDivElement>(state, item);
  const { hoverProps, isHovered } = useHover({});

  return (
    <div
      className={clsx(style.category, { [style.category_active]: isSelected, [style.category_hover]: isHovered })}
      ref={ref}
      {...mergeProps(tabProps, hoverProps)}
    >
      <div className={style.category_image} aria-hidden>
        <img src={getIconForPlaybookCategory({ imageName: item.props.imageName })?.large} alt={""} />
      </div>
      {rendered}
    </div>
  );
}

function TabPanel({ state, ...props }: { state: TabListState<ReactElement> }) {
  const navigate = useNavigate();
  const dispatch = useDispatch<ReduxDispatch>();
  const { ref, tabPanelProps } = useTabPanel<HTMLDivElement>(state, false, props);
  const [isLoading, setLoading] = useState(false);
  const isVisuallyLoading = useVisuallyLoading(isLoading);
  const [events, setEvents] = useState<PlaybookEvent[]>();

  const categoryId = useURLSearchParamSingleValue("category");

  const lastNotificationEvent = useSelector<IAppState, PlaybookEvent | null>(
    (state) => state.notifications.lastNotification?.playbookEvent,
    shallowEqual,
  );

  const loadPlaybooksEvents = useCallback(
    (key?: number, currentUserOnly = true) => {
      setEvents(undefined);
      setLoading(true);

      dispatch(loadEvents(12, key, 0, currentUserOnly))
        .then((events) => {
          setLoading(false);
          setEvents(events);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [dispatch],
  );

  useEffect(() => {
    // Loads even if key is not set (default when you visit the page for the first time)
    const key = state.selectedItem?.key ? Number(state.selectedItem?.key) : undefined;
    loadPlaybooksEvents(key);
    // reload the events if the last notification is related
  }, [loadPlaybooksEvents, state.selectedItem?.key, lastNotificationEvent]);

  return (
    <>
      {state.selectedItem?.key ? (
        <div {...tabPanelProps} ref={ref} className={style.playbooks_card}>
          {state.selectedItem?.props.children}
        </div>
      ) : (
        <div className={style.arrow}>
          <div className={style.arrow_text}>
            Select a category to show
            <br />
            related Playbooks
          </div>
          <SelectorPlaybooksArrow />
        </div>
      )}
      <div style={{ width: 1200, margin: "0 auto", maxWidth: "100%" }}>
        <Card
          title={"My Recent Playbook Events"}
          actions={[
            <ActionButton
              key={"playbooks-page-viewall"}
              onPress={() => {
                categoryId ? navigate(`/content/events?filterByCategory=${categoryId}`) : navigate(`/content/events`);
              }}
              label={"View All Events"}
            />,
          ]}
        >
          <div className={style.events}>
            {isVisuallyLoading && <LoadingSpinner />}
            {!isVisuallyLoading && events?.map((event) => <PlaybookEventTile event={event} key={event.id} />)}
            {!isVisuallyLoading && events && events.length === 0 && <ContentPlaceholder text={"No events"} />}
          </div>
        </Card>
      </div>
    </>
  );
}

const PlaybookCategories = (props: SingleSelectListProps<unknown>) => {
  const { ref, state, tabListProps } = useNoSelectionTabList<HTMLDivElement>(props);
  const { isFocusVisible, focusProps } = useFocusRing({ within: true });

  return (
    <div className={style.tabs}>
      <div
        className={clsx(style.categories, { [style.categories_focus]: isFocusVisible })}
        {...mergeProps(tabListProps, focusProps)}
        ref={ref}
      >
        {[...state.collection].map((item) => (
          <Tab key={item.key} item={item} state={state} />
        ))}
      </div>
      <TabPanel key={state.selectedItem?.key} state={state} />
    </div>
  );
};
const selectedCategoryAtom = atom<string | null>(null);

const PlaybooksPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ReduxDispatch>();

  const [categories, setCategories] = useState<Map<number, PlaybookCategory>>(new Map<number, PlaybookCategory>());
  const [groupedPlaybooks, setGroupedPlaybooks] = useState<Map<string, Playbook[][]>>(new Map<string, Playbook[][]>());

  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [isLoadingPlaybooks, setLoadingPlaybooks] = useState<boolean>(false);

  useEffect(() => {
    setLoadingPlaybooks(true);
    void dispatch(loadPlaybooks()).then((playbooks) => {
      const visiblePlaybooks = playbooks.filter(isPlaybookExecutableInPlaybookPageContext);
      setPlaybooks(visiblePlaybooks);
      setLoadingPlaybooks(false);
    });
  }, [configuration.license]);

  useEffect(() => {
    const ungroupedPlaybooks = new Map<string, Playbook[]>();
    const groupedPlaybooks = new Map<string, Playbook[][]>();
    const categories = new Map<number, PlaybookCategory>();

    // sort playbooks by category
    for (const playbook of playbooks) {
      for (const category of playbook.categories) {
        const existingPlaybooks = ungroupedPlaybooks.get(category.name) ?? [];
        existingPlaybooks.push(playbook);
        existingPlaybooks.sort((arg1, arg2) =>
          (arg1.name ?? "").localeCompare(arg2.name ?? "", "en", { sensitivity: "base" }),
        );
        ungroupedPlaybooks.set(category.name, existingPlaybooks);
        categories.set(Number(category.id), category);
      }
    }

    // group playbook by suffix
    for (const [category, playbooksList] of ungroupedPlaybooks) {
      const playbooksGroupByName = groupBy((playbook) => playbook.name, playbooksList);
      groupedPlaybooks.set(category, values(playbooksGroupByName as Record<string, Playbook[]>));
    }

    setCategories(categories);
    setGroupedPlaybooks(groupedPlaybooks);
  }, [playbooks]);

  const helpdeskEmail = useSelector<IAppState, string | null | undefined>(
    (state) => state.config.configuration.application?.helpdeskEmail,
  );

  const categoryId = useURLSearchParamSingleValue("category");
  const [selectedCategory, setSelectedCategory] = useAtom(selectedCategoryAtom);

  useDocumentTitle("Playbooks");

  // Replace url if a category is selected from the tabs or loaded from the currentCategory in redux.
  useEffect(() => {
    if (selectedCategory != null) {
      navigate(`/playbooks?category=${selectedCategory}`, { replace: true });
    } else if (categoryId != null) {
      setSelectedCategory(categoryId);
    }
  }, [selectedCategory, categoryId]);

  return (
    <div className={clsx(styleLayout.page, styleLayout.page_maybe_scrollable)}>
      <div className={style.header}>
        <div className={style.logo}>
          <img src={ModulesIcons.DEFAULT.large} alt="" aria-hidden />
          {/*<SelectorPlaybooksLogo />*/}
          Playbooks
        </div>
        <PlaybookFilter playbooks={playbooks} />
      </div>
      {isLoadingPlaybooks ? (
        <div className={style.tabs}>
          <div className={style.categories}>
            <LoadingSpinner />
          </div>
        </div>
      ) : categories.size === 0 ? (
        <div className={style.tabs}>
          <ContentPlaceholder>
            You have no permissions to execute any Playbook.
            <br />
            If you think this is an error, please contact your system{" "}
            <a href={`mailto:${helpdeskEmail}`}>administrator</a>.
          </ContentPlaceholder>
        </div>
      ) : (
        <PlaybookCategories
          aria-label={"Playbooks"}
          selectedKey={categoryId ? categoryId : (selectedCategory ?? null)}
          onSelectionChange={(key) => {
            if (selectedCategory != String(key)) {
              setSelectedCategory(String(key));
              navigate(`/playbooks?category=${key}`, { replace: true });
            } else {
              // If we click again on the same category, it deselects it.
              setSelectedCategory(null);
              navigate(`/playbooks`, { replace: true }); // need to reset categoryId first because of the useEffect above
            }
          }}
        >
          {Array.from(categories)
            .sort(([_id1, arg1], [_id2, arg2]) =>
              arg1.name.localeCompare(arg2.name ?? "", "en", { sensitivity: "base" }),
            )
            .map(([id, category]) => (
              <Item key={id} title={category.name} textValue={category.name} imageName={category.imageName}>
                <HeaderBar title={`${category.name} Playbooks`} style={{ minHeight: 40 }} />
                <div className={style.playbooks}>
                  {groupedPlaybooks.get(category.name ?? "")?.map((playbookGroup) => {
                    if (playbookGroup.length === 1) {
                      return <PlaybookLauncherTile playbook={playbookGroup[0]} key={playbookGroup[0].id} />;
                    } else {
                      return <PlaybookLauncherTileAsMenu playbooks={playbookGroup} key={playbookGroup[0].id} />;
                    }
                  })}
                </div>
              </Item>
            ))}
        </PlaybookCategories>
      )}
    </div>
  );
};

export { PlaybooksPage };
